
/* guards.jsx
* -------------------------------------------------------------------------------------------------
* Realm-aware route-guard helpers for React Router v6.
*
*     Because we use <HashRouter>, React Router automatically injects "#".  
*     Adding it manually creates malformed URLs such as "#/login#/home", so avoid that.
*
*   - Guards return a <LoadingBar /> while hydration is pending (token read from storage).
*
*   - When restricted, carrier guards redirect to "/carrier/login" or "/carrier/home"
*      instead of, for example, a malformed "#/carrier/login".
*
* NOTE:
*   These guards depend on the Auth contexts from:
*     - useUserAuth()
*     - useCarrierAuth()
*
*   Each provider must wrap the app (done in App.jsx).
*
**************************************************************************************************/

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

// Realm-specific authentication context hooks
import { useUserAuth } from './UserAuthProvider';
import { useCarrierAuth } from './CarrierAuthProvider';

// Optional loading indicator
import { LoadingBar } from '../../M_Components';

/* ================================================================================================
   USER-REALM GUARDS
   -----------------------------------------------------------------------------------------------
   These are used for routes under /home and /login.
   They read user auth (not carrier).
================================================================================================ */

/**
 * Protects a route so it can ONLY be accessed when a *user* is authenticated.
 * If the user is NOT authenticated -> redirect to "/login".
 *
 * Usage:
 *   <Route
 *     path="/home/*"
 *     element={
 *       <UserProtectedRoute>
 *         <HomePage />
 *       </UserProtectedRoute>
 *     }
 *   />
 */
export function UserProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useUserAuth();

  // While restoring auth from storage -> block UI flicker
  if (loading) return <LoadingBar />;

  // No valid session -> user must log in
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  // Valid session -> allow page
  return children;
}

/**
 * Blocks access to routes meant for logged-OUT *users*.
 * If the user IS authenticated -> redirect to "/home".
 *
 * Usage:
 *   <Route
 *     path="/login"
 *     element={
 *       <UserPublicOnlyRoute>
 *         <LoginPage />
 *       </UserPublicOnlyRoute>
 *     }
 *   />
 */
export function UserPublicOnlyRoute({ children }) {
  const { isAuthenticated, loading } = useUserAuth();

  if (loading) return <LoadingBar />;

  // Already logged in -> go to home
  if (isAuthenticated) return <Navigate to="/home" replace />;

  return children;
}

/* ================================================================================================
   CARRIER-REALM GUARDS
   -----------------------------------------------------------------------------------------------
   These are used for routes under /carrier/home and /carrier/login.
   They read carrier auth (not user).
================================================================================================ */

/**
 * Protects a route so it can ONLY be accessed when a *carrier* is authenticated.
 * If the carrier is NOT authenticated -> redirect to "/carrier/login".
 *
 * Usage:
 *   <Route
 *     path="/carrier/home/*"
 *     element={
 *       <CarrierProtectedRoute>
 *         <CarrierHomePage />
 *       </CarrierProtectedRoute>
 *     }
 *   />
 */
export function CarrierProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useCarrierAuth();
  const location = useLocation();

  // While restoring auth from storage:
  // - For the invite wrapper route, return null so we don't stack a second loader
  //   on top of the invite page's own LoadingBar splash.
  // - For other routes, keep the existing LoadingBar behavior.
  if (loading) {
    const isInviteWrapper =
      location.pathname === '/carrier/home/CarrierInviteViewBid' ||
      location.pathname.startsWith('/carrier/home/CarrierInviteViewBid/');

    if (isInviteWrapper) return null;

    return <LoadingBar />;
  }

  if (!isAuthenticated) return <Navigate to="/carrier/login" replace />;

  return children;
}


/**
 * Blocks access to routes meant for logged-OUT *carriers*.
 * If the carrier IS authenticated -> redirect to "/carrier/home".
 *
 * Usage:
 *   <Route
 *     path="/carrier/login"
 *     element={
 *       <CarrierPublicOnlyRoute>
 *         <CarrierLoginPage />
 *       </CarrierPublicOnlyRoute>
 *     }
 *   />
 */
export function CarrierPublicOnlyRoute({ children }) {
  const { isAuthenticated, loading } = useCarrierAuth();

  if (loading) return <LoadingBar />;

  // Already logged in -> go to home
  if (isAuthenticated) return <Navigate to="/carrier/home" replace />;

  return children;
}
